import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getAllAffiliates, getAllCommissions, getAllReferredSignups, getAffiliateAccountEmails, markAffiliateCommissionsPaid } from '../services/userDataService'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'

const CHART_REVENUE = '#3b82f6'
const CHART_COMMISSION = '#16a34a'

// Cosmetic gate only: the real lock is the admin RLS policies (non-admin
// requests get empty results from the DB regardless of what this renders)
const ADMIN_EMAIL = 'josephpalumbo02@gmail.com'

const PAYOUT_MINIMUM_CENTS = 2500

const css = `
.adm-root {
  min-height: 100vh;
  position: relative;
  background:
    radial-gradient(900px 420px at 75% -10%, rgba(59, 130, 246, 0.11), transparent 65%),
    radial-gradient(700px 420px at -5% 110%, rgba(168, 85, 247, 0.05), transparent 60%),
    #010714;
}
@keyframes admFadeUp {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.adm-fade { animation: admFadeUp 0.45s ease both; }
.adm-glass {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.045), rgba(255, 255, 255, 0.018));
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  box-shadow: 0 24px 60px -36px rgba(0, 0, 0, 0.65), inset 0 1px 0 rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}
.adm-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 999px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 500;
  padding: 7px 15px;
  white-space: nowrap;
}
.adm-kpis { display: grid; grid-template-columns: repeat(6, 1fr); }
.adm-kpi { padding: 20px 24px; min-width: 0; }
.adm-kpi + .adm-kpi { border-left: 1px solid rgba(255, 255, 255, 0.055); }
@media (max-width: 767px) {
  .adm-kpis { grid-template-columns: 1fr 1fr; }
  .adm-kpi + .adm-kpi { border-left: none; }
  .adm-kpi { border-top: 1px solid rgba(255, 255, 255, 0.055); }
  .adm-kpis .adm-kpi:nth-child(-n+2) { border-top: none; }
}
.adm-table-wrap { overflow-x: auto; margin: 6px -24px 0; padding: 0 24px; }
.adm-scroll { max-height: 480px; overflow-y: auto; }
.adm-scroll thead th {
  position: sticky;
  top: 0;
  background: #0e1626;
  z-index: 1;
}
.adm-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
.adm-scroll::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.08); border-radius: 4px; }
.adm-scroll::-webkit-scrollbar-track { background: transparent; }
.adm-table { width: 100%; border-collapse: collapse; }
.adm-table th {
  text-align: left;
  color: #4d5b74;
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 12px 18px 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  white-space: nowrap;
}
.adm-table td {
  color: #94a3b8;
  font-size: 13px;
  padding: 13px 18px 13px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.adm-table th:last-child, .adm-table td:last-child { padding-right: 0; }
.adm-table tbody tr { transition: background 0.15s ease; }
.adm-table tbody tr:hover { background: rgba(255, 255, 255, 0.022); }
.adm-table tbody tr:last-child td { border-bottom: none; padding-bottom: 4px; }
.adm-table .num { text-align: right; }
.adm-mono { font-family: 'Fira Code', 'Consolas', monospace; font-size: 12px; letter-spacing: -0.2px; }
.adm-btn { transition: transform 0.15s ease, filter 0.15s ease, background 0.15s ease; cursor: pointer; }
.adm-btn:hover { filter: brightness(1.2); transform: translateY(-1px); }
.adm-btn:active { transform: translateY(0); }
.adm-due-row { transition: background 0.15s ease, border-color 0.15s ease; }
.adm-due-row:hover { background: rgba(255, 255, 255, 0.035) !important; border-color: rgba(255, 255, 255, 0.1) !important; }
`

const formatMoney = (cents) => '$' + (cents / 100).toFixed(2)

const formatDate = (iso) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

const payoutDetailsText = (a) => {
  if (a.payout_method === 'bank_au') {
    return a.payout_bsb && a.payout_account_number
      ? `BSB ${a.payout_bsb} · Acc ${a.payout_account_number}`
      : 'details missing'
  }
  return a.payout_email || 'email missing'
}

const payoutMethodLabel = (a) => (a.payout_method === 'bank_au' ? 'Bank' : 'PayPal')

const TIER_LABELS = { free: 'Free', sports: 'Sports', horse_racing: 'Promo', full_access: 'Full Access' }
const TIER_COLORS = { free: '#64748b', sports: '#60a5fa', horse_racing: '#a78bfa', full_access: '#34d399' }
const STATUS_COLORS = {
  active: '#4ade80',
  trialing: '#60a5fa',
  canceled: '#f87171',
  past_due: '#fbbf24',
  incomplete: '#fbbf24',
  unpaid: '#fbbf24'
}

const PILL = {
  pending: { color: '#fbbf24', bg: 'rgba(245, 158, 11, 0.09)', border: 'rgba(245, 158, 11, 0.22)' },
  payable: { color: '#60a5fa', bg: 'rgba(59, 130, 246, 0.09)', border: 'rgba(59, 130, 246, 0.22)' },
  paid: { color: '#4ade80', bg: 'rgba(34, 197, 94, 0.09)', border: 'rgba(34, 197, 94, 0.22)' },
  void: { color: '#64748b', bg: 'rgba(100, 116, 139, 0.09)', border: 'rgba(100, 116, 139, 0.22)' },
  approved: { color: '#4ade80', bg: 'rgba(34, 197, 94, 0.09)', border: 'rgba(34, 197, 94, 0.22)' },
  disabled: { color: '#f87171', bg: 'rgba(239, 68, 68, 0.09)', border: 'rgba(239, 68, 68, 0.22)' }
}

const Pill = ({ status }) => {
  const s = PILL[status] || PILL.void
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      color: s.color,
      background: s.bg,
      border: `1px solid ${s.border}`,
      borderRadius: '999px',
      padding: '3px 10px 3px 9px',
      fontSize: '10px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.6px',
      lineHeight: '14px'
    }}>
      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
      {status}
    </span>
  )
}

const SectionHead = ({ title, hint }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px' }}>
    <span style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: 600, letterSpacing: '-0.2px' }}>{title}</span>
    {hint && <span style={{ color: '#42506a', fontSize: '12px', whiteSpace: 'nowrap' }}>{hint}</span>}
  </div>
)

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth()
  const [affiliates, setAffiliates] = useState([])
  const [commissions, setCommissions] = useState([])
  const [signups, setSignups] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [confirmPayId, setConfirmPayId] = useState(null)
  const [payingId, setPayingId] = useState(null)
  const [expandedCode, setExpandedCode] = useState(null)
  const [accountEmails, setAccountEmails] = useState({})
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640)

  const isAdmin = user?.email === ADMIN_EMAIL

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 640)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  const loadData = useCallback(async () => {
    // Don't even fire the requests for non-admins (RLS would return nothing anyway)
    if (!user || user.email !== ADMIN_EMAIL) return
    const [affRes, comRes, signupRes] = await Promise.all([
      getAllAffiliates(),
      getAllCommissions(),
      getAllReferredSignups()
    ])
    setAffiliates(affRes.data || [])
    setCommissions(comRes.data || [])
    setSignups(signupRes.data || [])

    // Account emails for the affiliates table (payout_email can differ)
    const affiliateIds = (affRes.data || []).map((a) => a.user_id)
    if (affiliateIds.length > 0) {
      const { data: emailRows } = await getAffiliateAccountEmails(affiliateIds)
      const emailMap = {}
      ;(emailRows || []).forEach((r) => { emailMap[r.user_id] = r.email })
      setAccountEmails(emailMap)
    }
    setLoaded(true)
  }, [user])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleMarkPaid = async (affiliateId) => {
    setPayingId(affiliateId)
    await markAffiliateCommissionsPaid(affiliateId)
    await loadData()
    setPayingId(null)
    setConfirmPayId(null)
  }

  if (authLoading || (user && isAdmin && !loaded)) {
    return <div className="adm-root"><style>{css}</style></div>
  }

  if (!user || !isAdmin) {
    return (
      <div className="adm-root" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{css}</style>
        <p style={{ color: '#334155', fontSize: '14px' }}>Nothing to see here.</p>
      </div>
    )
  }

  const now = Date.now()

  const signupCounts = {}
  signups.forEach((row) => {
    signupCounts[row.referred_by] = (signupCounts[row.referred_by] || 0) + 1
  })

  const affiliateStats = affiliates.map((a) => {
    const rows = commissions.filter((c) => c.affiliate_id === a.id)
    const pendingCents = rows
      .filter((c) => c.status === 'pending' && new Date(c.matures_at).getTime() > now)
      .reduce((sum, c) => sum + c.commission_cents, 0)
    const payableCents = rows
      .filter((c) => c.status === 'pending' && new Date(c.matures_at).getTime() <= now)
      .reduce((sum, c) => sum + c.commission_cents, 0)
    const paidCents = rows
      .filter((c) => c.status === 'paid')
      .reduce((sum, c) => sum + c.commission_cents, 0)
    return { ...a, pendingCents, payableCents, paidCents, signups: signupCounts[a.code] || 0 }
  })

  const duePayouts = affiliateStats.filter((a) => a.payableCents >= PAYOUT_MINIMUM_CENTS && a.status === 'approved')
  const affiliateById = {}
  affiliates.forEach((a) => { affiliateById[a.id] = a })

  const totals = affiliateStats.reduce(
    (acc, a) => ({
      pending: acc.pending + a.pendingCents,
      payable: acc.payable + a.payableCents,
      paid: acc.paid + a.paidCents
    }),
    { pending: 0, payable: 0, paid: 0 }
  )

  const displayStatus = (c) =>
    c.status === 'pending' && new Date(c.matures_at).getTime() <= now ? 'payable' : c.status

  const revenueAllTimeCents = commissions
    .filter((c) => c.status !== 'void')
    .reduce((sum, c) => sum + c.amount_paid_cents, 0)

  const kpis = [
    { label: 'Affiliates', value: affiliates.length.toString(), dot: '#64748b' },
    { label: 'Referred signups', value: signups.length.toString(), dot: '#64748b' },
    { label: 'Revenue all time', value: formatMoney(revenueAllTimeCents), dot: '#a78bfa' },
    { label: 'Pending', value: formatMoney(totals.pending), dot: '#f59e0b' },
    { label: 'Payable now', value: formatMoney(totals.payable), dot: '#3b82f6' },
    { label: 'Paid all time', value: formatMoney(totals.paid), dot: '#22c55e' }
  ]

  // Monthly revenue vs commission, last 12 months (void rows excluded)
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const chartBuckets = []
  const chartMap = {}
  const today = new Date()
  for (let i = 11; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1)
    const bucket = {
      key: `${d.getFullYear()}-${d.getMonth()}`,
      label: d.getMonth() === 0 ? `Jan '${String(d.getFullYear()).slice(2)}` : monthNames[d.getMonth()],
      revenue: 0,
      commission: 0
    }
    chartBuckets.push(bucket)
    chartMap[bucket.key] = bucket
  }
  commissions.forEach((c) => {
    if (c.status === 'void') return
    const d = new Date(c.created_at)
    const bucket = chartMap[`${d.getFullYear()}-${d.getMonth()}`]
    if (bucket) {
      bucket.revenue += c.amount_paid_cents / 100
      bucket.commission += c.commission_cents / 100
    }
  })
  const hasChartData = chartBuckets.some((b) => b.revenue > 0)

  const endLabel = (name, color, dy) => (props) => {
    if (props.index !== chartBuckets.length - 1) return null
    return (
      <text x={props.x + 8} y={props.y + dy} fill={color} fontSize={11} fontWeight={600}>
        {name}
      </text>
    )
  }

  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null
    return (
      <div style={{
        background: '#141d33',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '9px',
        padding: '10px 13px',
        boxShadow: '0 12px 30px -12px rgba(0, 0, 0, 0.6)'
      }}>
        <p style={{ color: '#5b6a85', margin: '0 0 6px 0', fontSize: '11px', fontWeight: 500 }}>{label}</p>
        {payload.map((p) => (
          <p key={p.dataKey} style={{ color: '#e2e8f0', margin: '2px 0 0 0', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '7px', fontVariantNumeric: 'tabular-nums' }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: p.stroke, flexShrink: 0 }} />
            {p.dataKey === 'revenue' ? 'Revenue' : 'Commissions'}&nbsp; ${p.value.toFixed(2)}
          </p>
        ))}
      </div>
    )
  }

  const moneyCellColor = (cents, color) => (cents > 0 ? color : '#5b6a85')

  return (
    <div className="adm-root" style={{ padding: isMobile ? '28px 16px 64px' : '52px 24px 88px' }}>
      <style>{css}</style>
      <div className="adm-fade" style={{ maxWidth: '1080px', margin: '0 auto', position: 'relative', zIndex: 1 }}>

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'flex-end',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          gap: '14px',
          marginBottom: '24px'
        }}>
          <div>
            <div style={{ color: '#3b82f6', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px', marginBottom: '8px' }}>
              Admin
            </div>
            <h1 style={{ color: '#f1f5f9', fontSize: '26px', fontWeight: 650, margin: 0, letterSpacing: '-0.4px' }}>
              Affiliate program
            </h1>
            <p style={{ color: '#5b6a85', fontSize: '13px', margin: '6px 0 0 0' }}>
              Overview, referrals and monthly payouts
            </p>
          </div>
          <span className="adm-chip">
            Payday · 15th of each month
          </span>
        </div>

        {/* KPI band */}
        <div className="adm-glass adm-kpis" style={{ marginBottom: '14px', overflow: 'hidden' }}>
          {kpis.map((k) => (
            <div key={k.label} className="adm-kpi">
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '10px' }}>
                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: k.dot, flexShrink: 0 }} />
                <span style={{ color: '#5b6a85', fontSize: '11px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.7px', whiteSpace: 'nowrap' }}>
                  {k.label}
                </span>
              </div>
              <div style={{ color: '#f1f5f9', fontSize: '23px', fontWeight: 650, letterSpacing: '-0.6px', fontVariantNumeric: 'tabular-nums' }}>
                {k.value}
              </div>
            </div>
          ))}
        </div>

        {/* Revenue vs commissions, monthly */}
        <div className="adm-glass" style={{ padding: '24px', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: 600, letterSpacing: '-0.2px' }}>
              Referred revenue &amp; commissions
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {[
                { name: 'Revenue', color: CHART_REVENUE },
                { name: 'Commissions', color: CHART_COMMISSION }
              ].map((s) => (
                <span key={s.name} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#7c8db0', fontSize: '12px', fontWeight: 500 }}>
                  <span style={{ width: '14px', height: '2px', borderRadius: '1px', background: s.color, flexShrink: 0 }} />
                  {s.name}
                </span>
              ))}
              <span style={{ color: '#42506a', fontSize: '12px' }}>last 12 months</span>
            </div>
          </div>
          {hasChartData ? (
            <div style={{ marginTop: '18px' }}>
              <ResponsiveContainer width="100%" height={230}>
                <LineChart data={chartBuckets} margin={{ top: 8, right: 92, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255, 255, 255, 0.045)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    fontSize={10.5}
                    tick={{ fill: '#4d5b74' }}
                    dy={6}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    fontSize={10.5}
                    tick={{ fill: '#4d5b74' }}
                    tickFormatter={(v) => '$' + v}
                    width={52}
                    allowDecimals={false}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'rgba(255, 255, 255, 0.12)', strokeWidth: 1 }} />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke={CHART_REVENUE}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                    label={endLabel('Revenue', CHART_REVENUE, -4)}
                  />
                  <Line
                    type="monotone"
                    dataKey="commission"
                    stroke={CHART_COMMISSION}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                    label={endLabel('Commissions', CHART_COMMISSION, 14)}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p style={{ color: '#42506a', fontSize: '13px', margin: '14px 0 0 0', lineHeight: 1.6 }}>
              No referred payments yet. The chart fills in as commissions come through.
            </p>
          )}
        </div>

        {/* Due for payout — gradient hairline when money is owed */}
        <div style={{
          background: duePayouts.length > 0
            ? 'linear-gradient(120deg, rgba(59, 130, 246, 0.55), rgba(168, 85, 247, 0.3) 45%, rgba(255, 255, 255, 0.07) 75%)'
            : 'transparent',
          borderRadius: '17px',
          padding: duePayouts.length > 0 ? '1px' : 0,
          marginBottom: '14px'
        }}>
          <div
            className={duePayouts.length > 0 ? undefined : 'adm-glass'}
            style={duePayouts.length > 0
              ? { background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(11, 17, 32, 0.98))', borderRadius: '16px', padding: '24px', backdropFilter: 'blur(14px)' }
              : { padding: '24px' }}
          >
            <SectionHead title="Due for payout" hint={`matured · $${PAYOUT_MINIMUM_CENTS / 100} minimum`} />
            {duePayouts.length > 0 ? (
              <div style={{ marginTop: '18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {duePayouts.map((a) => (
                  <div key={a.id} className="adm-due-row" style={{
                    display: 'flex',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '12px' : '24px',
                    background: 'rgba(255, 255, 255, 0.025)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    borderRadius: '12px',
                    padding: '16px 20px'
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 600, letterSpacing: '-0.1px' }}>{a.code}</div>
                      <div style={{ color: '#5b6a85', fontSize: '12px', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ color: '#7c8db0', fontWeight: 500 }}>{payoutMethodLabel(a)}</span>
                        <span className="adm-mono">{payoutDetailsText(a)}</span>
                      </div>
                    </div>
                    <span style={{ color: '#60a5fa', fontSize: '21px', fontWeight: 700, letterSpacing: '-0.5px', fontVariantNumeric: 'tabular-nums' }}>
                      {formatMoney(a.payableCents)}
                    </span>
                    {confirmPayId === a.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#fbbf24', fontSize: '12px' }}>Sent the money?</span>
                        <button
                          className="adm-btn"
                          onClick={() => handleMarkPaid(a.id)}
                          disabled={payingId === a.id}
                          style={{ background: '#22c55e', border: 'none', borderRadius: '8px', color: '#052e13', padding: '8px 15px', fontSize: '12px', fontWeight: 600, opacity: payingId === a.id ? 0.6 : 1 }}
                        >
                          {payingId === a.id ? 'Marking…' : 'Yes, mark paid'}
                        </button>
                        <button
                          onClick={() => setConfirmPayId(null)}
                          style={{ background: 'none', border: 'none', color: '#5b6a85', cursor: 'pointer', padding: '8px 8px', fontSize: '12px', fontWeight: 500 }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        className="adm-btn"
                        onClick={() => setConfirmPayId(a.id)}
                        style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '8px', color: '#4ade80', padding: '8px 18px', fontSize: '12px', fontWeight: 600 }}
                      >
                        Mark paid
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#42506a', fontSize: '13px', margin: '12px 0 0 0', lineHeight: 1.6 }}>
                Nobody is due right now. Commissions become payable 30 days after each payment.
              </p>
            )}
          </div>
        </div>

        {/* All affiliates */}
        <div className="adm-glass" style={{ padding: '24px', marginBottom: '14px' }}>
          <SectionHead title="Affiliates" hint={`${affiliates.length} total · click a row to see referred members`} />
          {affiliateStats.length > 0 ? (
            <div className="adm-table-wrap adm-scroll">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th className="num">Signups</th>
                    <th className="num">Pending</th>
                    <th className="num">Payable</th>
                    <th className="num">Paid</th>
                    <th>Method</th>
                    <th>Payout details</th>
                    <th>Status</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {affiliateStats.map((a) => {
                    const isExpanded = expandedCode === a.code
                    const members = signups
                      .filter((s) => s.referred_by === a.code)
                      .sort((x, y) => new Date(y.created_at) - new Date(x.created_at))
                    return (
                      <React.Fragment key={a.id}>
                        <tr
                          onClick={() => setExpandedCode(isExpanded ? null : a.code)}
                          style={{ cursor: 'pointer', background: isExpanded ? 'rgba(255, 255, 255, 0.025)' : undefined }}
                        >
                          <td style={{ color: '#f1f5f9', fontWeight: 600 }}>
                            <span style={{ color: '#4d5b74', marginRight: '8px', fontSize: '10px', display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease' }}>▶</span>
                            {a.code}
                            {accountEmails[a.user_id] && (
                              <div className="adm-mono" style={{ color: '#5b6a85', fontSize: '11px', fontWeight: 400, marginTop: '3px', paddingLeft: '18px' }}>
                                {accountEmails[a.user_id]}
                              </div>
                            )}
                          </td>
                          <td className="num">{a.signups}</td>
                          <td className="num" style={{ color: moneyCellColor(a.pendingCents, '#fbbf24') }}>{formatMoney(a.pendingCents)}</td>
                          <td className="num" style={{ color: moneyCellColor(a.payableCents, '#60a5fa'), fontWeight: a.payableCents > 0 ? 600 : 400 }}>
                            {formatMoney(a.payableCents)}
                          </td>
                          <td className="num" style={{ color: moneyCellColor(a.paidCents, '#4ade80') }}>{formatMoney(a.paidCents)}</td>
                          <td>{payoutMethodLabel(a)}</td>
                          <td><span className="adm-mono">{payoutDetailsText(a)}</span></td>
                          <td><Pill status={a.status} /></td>
                          <td style={{ color: '#5b6a85' }}>{formatDate(a.created_at)}</td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={9} style={{ padding: '4px 0 16px 22px', whiteSpace: 'normal' }}>
                              {members.length > 0 ? (
                                <div style={{
                                  background: 'rgba(0, 0, 0, 0.22)',
                                  border: '1px solid rgba(255, 255, 255, 0.055)',
                                  borderRadius: '10px',
                                  padding: '6px 16px'
                                }}>
                                  <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(220px, 1.5fr) 110px 100px 120px 110px 110px',
                                    gap: '10px',
                                    padding: '8px 0 7px',
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                                    color: '#4d5b74',
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.7px'
                                  }}>
                                    <span>Member</span>
                                    <span>Plan</span>
                                    <span>Status</span>
                                    <span>Joined</span>
                                    <span style={{ textAlign: 'right' }}>Revenue</span>
                                    <span style={{ textAlign: 'right' }}>Commission</span>
                                  </div>
                                  {members.map((m, mi) => {
                                    const memberRows = commissions.filter(
                                      (c) => c.affiliate_id === a.id && c.referred_email === m.email && c.status !== 'void'
                                    )
                                    const revenueCents = memberRows.reduce((sum, c) => sum + c.amount_paid_cents, 0)
                                    const earnedCents = memberRows.reduce((sum, c) => sum + c.commission_cents, 0)
                                    return (
                                      <div key={m.email} style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'minmax(220px, 1.5fr) 110px 100px 120px 110px 110px',
                                        gap: '10px',
                                        alignItems: 'center',
                                        padding: '8px 0',
                                        borderBottom: mi < members.length - 1 ? '1px solid rgba(255, 255, 255, 0.035)' : 'none',
                                        fontSize: '12.5px',
                                        fontVariantNumeric: 'tabular-nums'
                                      }}>
                                        <span className="adm-mono" style={{ color: '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.email}</span>
                                        <span style={{ color: TIER_COLORS[m.subscription_tier] || '#64748b', fontWeight: 500 }}>
                                          {TIER_LABELS[m.subscription_tier] || 'Free'}
                                        </span>
                                        <span style={{ color: STATUS_COLORS[m.subscription_status] || '#64748b', fontSize: '12px' }}>
                                          {m.subscription_status || 'active'}
                                        </span>
                                        <span style={{ color: '#5b6a85', fontSize: '12px' }}>{formatDate(m.created_at)}</span>
                                        <span style={{ textAlign: 'right', color: revenueCents > 0 ? '#cbd5e1' : '#42506a' }}>
                                          {formatMoney(revenueCents)}
                                        </span>
                                        <span style={{ textAlign: 'right', color: earnedCents > 0 ? '#4ade80' : '#42506a', fontWeight: earnedCents > 0 ? 600 : 400 }}>
                                          {formatMoney(earnedCents)}
                                        </span>
                                      </div>
                                    )
                                  })}
                                </div>
                              ) : (
                                <span style={{ color: '#42506a', fontSize: '12.5px' }}>No referred members yet.</span>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: '#42506a', fontSize: '13px', margin: '12px 0 0 0' }}>No affiliates yet.</p>
          )}
        </div>

        {/* Full ledger */}
        <div className="adm-glass" style={{ padding: '24px' }}>
          <SectionHead title="Commissions" hint={`${commissions.length} total`} />
          {commissions.length > 0 ? (
            <div className="adm-table-wrap adm-scroll">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Affiliate</th>
                    <th>Referred customer</th>
                    <th className="num">Payment</th>
                    <th className="num">Commission</th>
                    <th>Status</th>
                    <th>Matures</th>
                    <th>Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map((c) => {
                    const status = displayStatus(c)
                    return (
                      <tr key={c.id}>
                        <td style={{ color: '#5b6a85' }}>{formatDate(c.created_at)}</td>
                        <td style={{ color: '#f1f5f9', fontWeight: 600 }}>
                          {affiliateById[c.affiliate_id]?.code || '—'}
                        </td>
                        <td>{c.referred_email || '—'}</td>
                        <td className="num">{formatMoney(c.amount_paid_cents)}</td>
                        <td className="num" style={{ color: '#f1f5f9', fontWeight: 600 }}>{formatMoney(c.commission_cents)}</td>
                        <td><Pill status={status} /></td>
                        <td style={{ color: '#5b6a85' }}>{formatDate(c.matures_at)}</td>
                        <td style={{ color: '#5b6a85' }}>{formatDate(c.paid_at)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: '#42506a', fontSize: '13px', margin: '12px 0 0 0' }}>No commissions yet.</p>
          )}
        </div>

        <p style={{ color: '#334155', fontSize: '12px', marginTop: '22px', lineHeight: 1.6 }}>
          Voiding a commission (refund/chargeback) and disabling an affiliate are deliberately SQL-only,
          see the runbook in affiliate_schema.sql.
        </p>

      </div>
    </div>
  )
}
